/**
 * Store Pinia : notifications in-app (Phase 1, polling 30s).
 *
 * - Polling adaptatif : 30s si document visible, en pause si onglet caché.
 * - Méthode `startPolling()` au login, `stopPolling()` au logout.
 * - Cache la liste des 30 dernières + compteur unread.
 */

import { defineStore } from 'pinia';
import { notificationsApi } from '../api/endpoints';
import type { AppNotification, NotificationPreferences } from '../types/models';

interface State {
  notifications: AppNotification[];
  unreadCount: number;
  preferences: NotificationPreferences | null;
  loading: boolean;
  error: string | null;
  pollTimer: ReturnType<typeof setTimeout> | null;
  visibilityListener: (() => void) | null;
}

const POLL_INTERVAL_MS = 30_000;

function extractErrorMessage(e: unknown): string {
  if (e && typeof e === 'object') {
    const obj = e as Record<string, unknown>;
    const response = obj.response as Record<string, unknown> | undefined;
    if (response) {
      const data = response.data as Record<string, unknown> | undefined;
      if (data?.message) return String(data.message);
    }
    if (typeof obj.message === 'string') return obj.message;
  }
  return 'unknown_error';
}

export const useNotificationsStore = defineStore('notifications', {
  state: (): State => ({
    notifications: [],
    unreadCount: 0,
    preferences: null,
    loading: false,
    error: null,
    pollTimer: null,
    visibilityListener: null,
  }),

  getters: {
    hasUnread: (state): boolean => state.unreadCount > 0,
    /** Badge à afficher (max 9+ pour limiter à 2 chiffres). */
    badgeLabel: (state): string => {
      if (state.unreadCount === 0) return '';
      if (state.unreadCount > 9) return '9+';
      return String(state.unreadCount);
    },
  },

  actions: {
    async fetchList(unreadOnly = false): Promise<void> {
      this.loading = true;
      this.error = null;
      try {
        const { notifications, unread_count } = await notificationsApi.list(unreadOnly, 30);
        this.notifications = notifications;
        this.unreadCount = unread_count;
      } catch (e) {
        this.error = extractErrorMessage(e);
        // Silent fail : si le polling échoue ponctuellement, on ne casse pas l'UI.
      } finally {
        this.loading = false;
      }
    },

    async markRead(id: number): Promise<void> {
      try {
        await notificationsApi.markRead(id);
        const notif = this.notifications.find((n) => n.id === id);
        if (notif && !notif.is_read) {
          notif.is_read = true;
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        }
      } catch (e) {
        this.error = extractErrorMessage(e);
      }
    },

    async markAllRead(): Promise<void> {
      try {
        await notificationsApi.markAllRead();
        for (const n of this.notifications) n.is_read = true;
        this.unreadCount = 0;
      } catch (e) {
        this.error = extractErrorMessage(e);
      }
    },

    async fetchPreferences(): Promise<void> {
      try {
        const { preferences } = await notificationsApi.getPreferences();
        this.preferences = preferences;
      } catch (e) {
        this.error = extractErrorMessage(e);
      }
    },

    async updatePreferences(prefs: Partial<NotificationPreferences>): Promise<void> {
      try {
        await notificationsApi.updatePreferences(prefs);
        if (this.preferences) {
          Object.assign(this.preferences, prefs);
        }
      } catch (e) {
        this.error = extractErrorMessage(e);
        throw e;
      }
    },

    /**
     * Demarre le polling 30s. Pause automatique si l'onglet est masque
     * (document.hidden) pour eviter le trafic inutile.
     */
    startPolling(): void {
      if (this.pollTimer) return; // deja en cours

      const tick = async (): Promise<void> => {
        if (typeof document === 'undefined' || !document.hidden) {
          await this.fetchList();
        }
        this.pollTimer = setTimeout(tick, POLL_INTERVAL_MS);
      };

      // Premier fetch immediat
      void this.fetchList();
      this.pollTimer = setTimeout(tick, POLL_INTERVAL_MS);

      // Visibilite : refresh immediat quand l'onglet redevient visible
      if (typeof document !== 'undefined') {
        this.visibilityListener = () => {
          if (!document.hidden) void this.fetchList();
        };
        document.addEventListener('visibilitychange', this.visibilityListener);
      }
    },

    stopPolling(): void {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
      if (this.visibilityListener && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', this.visibilityListener);
        this.visibilityListener = null;
      }
    },

    reset(): void {
      this.stopPolling();
      this.notifications = [];
      this.unreadCount = 0;
      this.preferences = null;
      this.error = null;
      this.loading = false;
    },
  },
});
